import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, Save, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const AXIOS_OPTS = { withCredentials: true };

export default function AdminBikeCompatibility() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [rebuilding, setRebuilding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/bikes/admin/review-queue`, {
        params: { limit: 500 },
        withCredentials: true,
      });
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error('Errore nel caricamento della coda di revisione');
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const r = await axios.post(
        `${API}/api/bikes/admin/rebuild-index`,
        {},
        AXIOS_OPTS
      );
      toast.success(`Reindicizzati ${r.data.inserted} record (${r.data.needs_review} da rivedere)`);
      await load();
    } catch (e) {
      toast.error('Errore durante la reindicizzazione');
    }
    setRebuilding(false);
  };

  const onEdit = (key, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const save = async (item) => {
    const key = `${item.sku}::${item.raw}`;
    const current = { ...item, ...(edits[key] || {}) };
    if (!current.brand || !current.model) {
      toast.error('Marca e Modello sono obbligatori');
      return;
    }
    setSaving((prev) => ({ ...prev, [key]: true }));
    try {
      await axios.patch(
        `${API}/api/bikes/admin/compatibility`,
        {
          sku: current.sku,
          raw: current.raw,
          brand: current.brand,
          model: current.model,
          year_from: current.year_from ? parseInt(current.year_from) : null,
          year_to: current.year_to ? parseInt(current.year_to) : null,
        },
        AXIOS_OPTS
      );
      toast.success('Record aggiornato');
      setItems((prev) => prev.filter((i) => !(i.sku === item.sku && i.raw === item.raw)));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error('Errore nel salvataggio');
    }
    setSaving((prev) => ({ ...prev, [key]: false }));
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/admin/inventory"
            className="flex items-center gap-2 text-white/60 hover:text-primary transition-colors"
          >
            <ArrowLeft size={18} /> Torna all'inventario
          </Link>
          <button
            onClick={rebuild}
            disabled={rebuilding}
            className="ml-auto flex items-center gap-2 bg-primary/20 hover:bg-primary/30 border border-primary/40 px-4 py-2 rounded-lg text-sm disabled:opacity-50"
            data-testid="rebuild-bike-index-btn"
          >
            <RefreshCw size={16} className={rebuilding ? 'animate-spin' : ''} />
            {rebuilding ? 'Reindicizzo…' : 'Reindicizza da zero'}
          </button>
        </div>

        <h1 className="text-3xl font-black uppercase mb-2">Compatibilità Moto — Revisione</h1>
        <p className="text-white/60 mb-6 text-sm">
          Qui trovi le voci di compatibilità moto che il parser automatico non ha
          saputo interpretare al 100%. Correggi marca, modello e anni; dopo
          il salvataggio le voci rientrano nel filtro "Trova ricambi per moto".
        </p>

        <div className="mb-4 flex items-center gap-3 text-sm">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            <AlertTriangle size={16} className="text-yellow-500" />
            <span className="text-white/80">Da rivedere:</span>
            <span className="text-primary font-bold" data-testid="review-total">{total}</span>
          </div>
          {total === 0 && !loading && (
            <div className="flex items-center gap-2 text-green-500 text-sm">
              <CheckCircle2 size={16} /> Tutto in ordine!
            </div>
          )}
        </div>

        {loading && <div className="text-center py-16 text-white/40">Caricamento…</div>}

        {!loading && items.length === 0 && (
          <div className="text-center py-16 text-white/40">
            Nessuna voce da rivedere.
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
              <thead className="bg-white/5">
                <tr className="text-left">
                  <th className="p-3">SKU</th>
                  <th className="p-3">Stringa originale</th>
                  <th className="p-3">Marca</th>
                  <th className="p-3">Modello</th>
                  <th className="p-3">Anno da</th>
                  <th className="p-3">Anno a</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const key = `${item.sku}::${item.raw}`;
                  const e = edits[key] || {};
                  const val = (f) => (e[f] !== undefined ? e[f] : (item[f] ?? ''));
                  return (
                    <tr key={key} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-3 font-mono text-xs text-white/70">{item.sku}</td>
                      <td className="p-3 text-white/80 max-w-xs truncate" title={item.raw}>{item.raw}</td>
                      <td className="p-3">
                        <input
                          type="text"
                          value={val('brand')}
                          onChange={(ev) => onEdit(key, 'brand', ev.target.value)}
                          className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-xs focus:border-primary focus:outline-none"
                          data-testid={`brand-${item.sku}`}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          type="text"
                          value={val('model')}
                          onChange={(ev) => onEdit(key, 'model', ev.target.value)}
                          className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-xs focus:border-primary focus:outline-none"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          type="number"
                          value={val('year_from')}
                          onChange={(ev) => onEdit(key, 'year_from', ev.target.value)}
                          className="w-20 bg-black/40 border border-white/15 rounded px-2 py-1 text-xs focus:border-primary focus:outline-none"
                          placeholder="2020"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          type="number"
                          value={val('year_to')}
                          onChange={(ev) => onEdit(key, 'year_to', ev.target.value)}
                          className="w-20 bg-black/40 border border-white/15 rounded px-2 py-1 text-xs focus:border-primary focus:outline-none"
                          placeholder="2024"
                        />
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => save(item)}
                          disabled={saving[key]}
                          className="flex items-center gap-1 bg-primary hover:bg-primary/90 text-black font-semibold px-3 py-1.5 rounded text-xs disabled:opacity-50"
                        >
                          <Save size={14} />
                          Salva
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
